import { ArrowRightOutlined } from '@ant-design/icons';
import { Flex, Tag } from 'antd';
import 'bootstrap/dist/js/bootstrap.bundle.min';
import Accordion from 'components/Accordion';
import CustomLoader from 'components/CustomLoader/index';
import StatusTag from 'components/StatusTag';
import TaskInfo from 'components/TaskInfo';
import { useMemo } from 'react';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import { Link } from 'react-router-dom';
import { routes } from 'routes/AllRoutes';
import { getPipePictureList } from 'screens/PipeReview/helper';
import { Pipe } from 'types/common';
import { renderDate, renderInfoValue, renderLocation } from 'utils/helpers';
import { SelectTaskData } from '../../../../sales-group-progress';
import useDryEventQuery from '../../../hooks/queries/useDryEventQuery';
import './style.scss';

type DryEventAccordionProps = {
  data: SelectTaskData;
  pipeData: Pipe;
  index: number;
};

const getDryingEventConfig = (dryEventNumber: number, pipeData: Pipe) => {
  switch (dryEventNumber) {
    case 1:
      return {
        statusKey: pipeData.dap30Status,
        reviewStatusKey: pipeData.reviewStatus,
        pictureTag: 'DRYING_EVENT',
        description: 'Drying Event 1',
      };
    case 2:
      return {
        statusKey: pipeData.secondDap30Status,
        reviewStatusKey: pipeData.secondReviewStatus,
        pictureTag: 'SECOND_DRYING_EVENT',
        description: 'Drying Event 2',
      };
    case 3:
      return {
        statusKey: pipeData.thirdDap30Status,
        reviewStatusKey: pipeData.thirdReviewStatus,
        pictureTag: 'THIRD_DRYING_EVENT',
        description: 'Drying Event 3',
      };
    default:
      return {};
  }
};

const getDryingEventNumber = (description?: string) => {
  switch (description) {
    case 'Drying Event 1':
    case 'Drying Event':
      return 1;
    case 'Drying Event 2':
      return 2;
    case 'Drying Event 3':
      return 3;
    default:
      return 1;
  }
};

const DryEventAccordion = ({
  data,
  pipeData,
  index,
}: DryEventAccordionProps) => {
  const { task, season } = { ...data };
  const dryingEventNumber = getDryingEventNumber(task?.description);
  const config = useMemo(
    () => getDryingEventConfig(dryingEventNumber, pipeData),
    [task],
  );

  const { data: { data: dryEventdata } = {}, isLoading } = useDryEventQuery(
    pipeData.id,
    task?.id,
  );

  const dryingEventStatus = config.statusKey;

  const reviewStatus = config?.reviewStatusKey;

  const pictures = useMemo(
    () =>
      getPipePictureList(dryEventdata)?.nonRejectedPictures?.filter(
        (pict) => pict.tag === config?.pictureTag,
      ) || [],
    [dryEventdata],
  );

  const rejectedPictures = useMemo(
    () =>
      getPipePictureList(dryEventdata)?.rejectedPictures?.filter(
        (pict) => pict.tag === config?.pictureTag,
      ) || [],
    [dryEventdata],
  );

  return (
    <Accordion
      title={`Pipe ${index + 1}`}
      className="pipe-accordion"
      headerContent={
        <Flex justify="space-between">
          <div className="accordion-content">
            <StatusTag
              status={
                dryingEventStatus === 'DONE' ? 'SERVICED' : 'NOT SERVICED'
              }
            />
            <StatusTag status={reviewStatus ?? ''} />
          </div>

          <div className="accordion-content">{`Serviced on:  ${!dryEventdata && dryingEventStatus !== 'DONE' ? 'N/A' : renderDate(dryEventdata?.createdAt, 'N/A', 'MMM DD, YYYY')}`}</div>
        </Flex>
      }
    >
      {isLoading ? (
        <div className="loading-container">
          <CustomLoader size="medium" />
        </div>
      ) : (
        <>
          {!dryEventdata ? (
            <span className="m-2">No data available</span>
          ) : (
            <>
              <Flex vertical gap={10}>
                <TaskInfo
                  header={'GPS'}
                  renderValue={() =>
                    renderLocation(dryEventdata?.mrvResourcesDto?.location)
                  }
                />

                <TaskInfo
                  header={'Water level'}
                  renderValue={() => renderInfoValue(dryEventdata?.waterLevel)}
                />

                <TaskInfo
                  header={'Photos'}
                  renderValue={() => {
                    if (pictures.length === 0) {
                      return 'No photos available';
                    }

                    return (
                      <Flex vertical gap={8}>
                        <PhotoProvider>
                          {pictures.map((image) => (
                            <div className="image-section" key={image?.id}>
                              <section className="image">
                                <PhotoView src={image?.url}>
                                  <img
                                    src={image?.url}
                                    alt="No Preview Available"
                                    className="pipe-img"
                                  />
                                </PhotoView>
                              </section>

                              <section className="dry-event-drawer-action">
                                {image?.status === 'APPROVED' && (
                                  <Tag className={'tag approved'}>Approved</Tag>
                                )}
                              </section>
                            </div>
                          ))}
                        </PhotoProvider>
                      </Flex>
                    );
                  }}
                />

                <TaskInfo
                  header={'Rejected Photos'}
                  renderValue={() => {
                    if (rejectedPictures.length === 0) {
                      return 'No photos available';
                    }

                    return (
                      <Flex vertical gap={8}>
                        <PhotoProvider>
                          {rejectedPictures.map((image) => (
                            <div className="image-section" key={image?.id}>
                              <section className="image">
                                <PhotoView src={image?.url}>
                                  <img
                                    src={image?.url}
                                    alt="No Preview Available"
                                    className="pipe-img"
                                  />
                                </PhotoView>
                              </section>
                            </div>
                          ))}
                        </PhotoProvider>

                        <Link
                          to={{
                            pathname: routes.pipeReview,
                            search: `seasonId=${season?.id}&documentId=${dryEventdata?.documentList?.[0]?.id}`,
                          }}
                          className="link-text"
                        >
                          Review Pipe Pictures <ArrowRightOutlined />
                        </Link>
                      </Flex>
                    );
                  }}
                />

                <TaskInfo
                  header={'Notes'}
                  valueClass="pipe-details-field-value"
                  renderValue={() =>
                    renderInfoValue(dryEventdata?.notes, 'N/A')
                  }
                />
              </Flex>
            </>
          )}
        </>
      )}
    </Accordion>
  );
};

export default DryEventAccordion;
